"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DotsBackgroundWhite from "@/components/career/DotsBackgroundWhite";

import { PageCustomersSchema } from "@/sanity/pages/PageCustomersTemplate";

type FeatureTab = PageCustomersSchema["featuresTabs"][number];

const FALLBACK_ACCENT = "#D9F9B9";

const TAB_RESTING = "#FFFFFF";

const fadeInUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

function fade(color: string, suffix: string) {
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${suffix}` : color;
}

function wash(tab: FeatureTab) {
  return tab?.calloutColor || fade(tab?.color || FALLBACK_ACCENT, "14");
}

function LazyFeatureVideo({ tab }: { tab: FeatureTab }) {
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100%" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="absolute inset-0">
      {ready ? (
        <HLSVideoPlayer
          source={tab.video}
          autoPlay
          muted
          loop
          playsInline
          poster={tab?.video?.thumbnail?.src}
          className="absolute top-0 left-0 !h-full !w-full object-cover"
        />
      ) : (
        tab?.video?.thumbnail?.src && (
          <Image
            className="absolute top-0 left-0 !h-full !w-full object-cover"
            src={tab.video.thumbnail}
            alt=""
          />
        )
      )}
    </div>
  );
}

function TabPanel({ tab }: { tab: FeatureTab }) {
  const accent = tab?.color || FALLBACK_ACCENT;
  const strong = tab?.accentColor || accent;

  const isVideo = stegaClean(tab?.mediaType) === "video";
  const hasVideo = tab?.video?.muxVideo?.playbackId || tab?.video?.embedUrl;
  const hasMedia = isVideo ? hasVideo : tab?.image?.src;

  return (
    <motion.div
      {...fadeInUp(0.1)}
      viewport={{ once: true, margin: "100px" }}
      className="flex flex-1 flex-col gap-[2rem] px-[3rem] pt-[4rem] pb-[2.25rem] lg:flex-row lg:gap-[11rem] lg:p-[2.25rem]">
      <div className="flex flex-col justify-between gap-[4rem] lg:min-h-[66.375rem] lg:w-[73.875rem] lg:p-[3rem]">
        <div className="flex w-full flex-col gap-[2rem] lg:gap-[3rem]">
          <div className="flex w-full flex-col gap-[3rem]">
            <div className="flex flex-col gap-[3rem] leading-[1.4] text-black">
              {tab?.eyebrow && (
                <p className="m-0 hidden font-geist text-[2.5rem] font-normal opacity-70 [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] lg:block">
                  {tab.eyebrow}
                </p>
              )}
              {tab?.title && (
                <p className="m-0 font-geist text-[3.25rem] leading-[1.1] font-medium [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] lg:text-[4.75rem] lg:leading-[1.4]">
                  {tab.title}
                </p>
              )}
            </div>

            {tab?.tags?.length > 0 && (
              <div className="hidden flex-wrap items-start gap-[.625rem] lg:flex">
                {tab.tags.map((tag, index) => (
                  <div
                    key={index}
                    className="flex items-start rounded-[5rem] border border-black/[.07] bg-[#F8F7F6] p-[2rem]">
                    <p className="m-0 font-geistmono text-[1.75rem] leading-[1.5] font-medium whitespace-nowrap text-[#1F2F12] uppercase [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                      {tag}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="h-px w-full bg-black/10" />
          </div>

          <div className="flex w-full flex-col gap-[4rem]">
            {tab?.text && (
              <p className="m-0 font-geist text-[2rem] leading-[1.5] font-normal text-black/60 [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] lg:max-w-[64.5rem]">
                {tab.text}
              </p>
            )}

            {tab?.bullets?.length > 0 && (
              <div className="flex w-full flex-col gap-[1.5rem] lg:gap-[3rem]">
                {tab.bullets.map((item, index) => (
                  <div
                    key={index}
                    className={cn(
                      "flex w-full items-center gap-[1rem]",
                      index >= 2 && "hidden lg:flex",
                    )}>
                    <span
                      className="block h-[1.5rem] w-[1.5rem] shrink-0 [clip-path:polygon(50%_0,100%_50%,100%_100%,0_100%,0_0)]"
                      style={{ backgroundColor: accent }}
                      aria-hidden
                    />
                    <p className="m-0 flex-1 font-geist text-[2rem] leading-[1.5] font-normal text-black/60 [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {(tab?.calloutTitle || tab?.calloutText) && (
          <div
            className="hidden items-start lg:flex lg:w-[64.25rem]"
            style={{ backgroundColor: wash(tab) }}>
            <div
              className="w-[.25rem] shrink-0 self-stretch"
              style={{ backgroundColor: strong }}
            />
            <div className="flex flex-1 flex-col justify-center gap-[1.5rem] px-[1.5rem] py-[2rem]">
              {tab?.calloutTitle && (
                <p className="m-0 w-full font-geistmono text-[1.75rem] leading-[1.5] font-medium text-black uppercase [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                  {tab.calloutTitle}
                </p>
              )}
              {tab?.calloutText && (
                <p className="m-0 w-full font-geist text-[2rem] leading-[1.3] font-normal text-black/50 [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                  {tab.calloutText}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {hasMedia && (
        <div className="relative mt-auto aspect-[486/531] w-full overflow-clip lg:mt-0 lg:aspect-auto lg:min-h-[66.375rem] lg:w-[60.75rem]">
          {isVideo ? (
            <LazyFeatureVideo tab={tab} />
          ) : (
            <Image
              className="absolute top-0 left-0 !h-full !w-full object-cover"
              src={tab.image}
              alt=""
            />
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function FeaturesSection({ data, className }: { data: PageCustomersSchema; className?: string }) {
  const tabs = data?.featuresTabs ?? [];

  const headerRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const tabsRowRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [active, setActive] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const measure = () => {
      const header = headerRef.current;
      if (!header) return;

      const h = header.offsetHeight;
      const card = Math.max(0, ...cardRefs.current.map((node) => node?.offsetHeight ?? 0));

      setHeaderHeight(h);
      setOffset(Math.max((window.innerHeight - (h + card)) / 2, 0));
    };
    measure();

    const observer = new ResizeObserver(measure);
    if (headerRef.current) observer.observe(headerRef.current);
    cardRefs.current.forEach((node) => node && observer.observe(node));
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [tabs.length]);

  useEffect(() => {
    if (tabs.length === 0 || !isDesktop) return;

    let frame = 0;

    const update = () => {
      frame = 0;

      const header = headerRef.current;
      if (!header) return;

      const line = header.getBoundingClientRect().bottom;
      const cards = cardRefs.current;

      let index = 0;
      for (let i = 0; i < tabs.length; i++) {
        const card = cards[i];
        if (card && card.getBoundingClientRect().top <= line + 2) index = i;
      }

      setActive(index);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [tabs.length, isDesktop]);

  useEffect(() => {
    if (tabs.length === 0 || isDesktop) return;

    const stack = stackRef.current;
    if (!stack) return;

    let frame = 0;

    const update = () => {
      frame = 0;

      const x = stack.scrollLeft;
      let index = 0;
      let best = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const distance = Math.abs(card.offsetLeft - x);
        if (distance < best) {
          best = distance;
          index = i;
        }
      });

      setActive(index);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    stack.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      stack.removeEventListener("scroll", onScroll);
    };
  }, [tabs.length, isDesktop]);

  useEffect(() => {
    if (isDesktop) return;

    const row = tabsRowRef.current;
    const el = row?.children[active] as HTMLElement | undefined;
    if (!row || !el) return;

    row.scrollTo({ left: Math.max(el.offsetLeft - row.offsetLeft - 8, 0), behavior: "smooth" });
  }, [active, isDesktop]);

  const goToCard = useCallback(
    (index: number) => {
      const stack = stackRef.current;
      const card = cardRefs.current[index];
      if (!stack || !card) return;

      if (!isDesktop) {
        stack.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
        return;
      }

      const gap = parseFloat(getComputedStyle(stack).rowGap) || 0;

      let top = stack.getBoundingClientRect().top + window.scrollY;
      for (let i = 0; i < index; i++) top += (cardRefs.current[i]?.offsetHeight ?? 0) + gap;

      window.scrollTo({
        top: Math.max(top - (offset + headerHeight) + 1, 0),
        behavior: "smooth",
      });
    },
    [isDesktop, offset, headerHeight],
  );

  return (
    <section
      className={cn("relative bg-[#F8F7F6] px-[2rem]  pb-[10rem] sm:px-[3rem] md:px-[5rem]  lg:pb-[14rem]", className)}>
      <DecorationLine
        className="top-0 left-[1rem] lg:left-[8.85%]"
        orientation="vertical"
        style="dark"
      />
      <DecorationLine
        className="top-0 right-[1rem] lg:right-[8.85%]"
        orientation="vertical"
        style="dark"
      />

      <div className="relative z-[1] mx-auto flex h-fit w-[min(150.125rem,100%)] flex-col items-center overflow-clip">
        {/* <div className="pointer-events-none absolute inset-x-0 top-0 h-[26rem] overflow-clip"> */}
        {/*   <DotsBackgroundWhite className="absolute top-0 left-1/2 -translate-x-1/2" /> */}
        {/* </div> */}
        <div className="relative z-[4] flex w-full flex-col items-center pt-[4rem] lg:pt-[10rem]">
          {data?.featuresChip?.text && (
            <motion.div {...fadeInUp()}>
              <Chip
                className="mb-[2rem] [&_img]:!w-[2rem] [&_p]:font-geistmono [&_p]:text-[#202223] [&_p]:uppercase"
                image={data?.featuresChip?.icon}
                text={data?.featuresChip?.text}
              />
            </motion.div>
          )}

          {data?.featuresTitle && (
            <motion.div
              {...fadeInUp(0.1)}
              className="pixel-highlight richtext richtext-center w-[46rem] lg:w-[min(112rem,100%)] [&_br]:hidden lg:[&_br]:block prose-em:ml-[1rem] prose-em:!px-0 prose-em:font-ivy lg:prose-em:!px-[.7rem] prose-headings:mb-0 prose-headings:text-[4.5rem] prose-headings:leading-[1.1] lg:prose-headings:text-[6.5rem]">
              <PortableText
                value={data.featuresTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.featuresText && (
            <motion.div
              {...fadeInUp(0.2)}
              className="richtext richtext-center mt-[1rem] w-[39.75rem] lg:mt-[3.125rem] lg:w-[min(69.25rem,100%)] prose-p:mb-0 prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/60">
              <PortableText
                value={data.featuresText}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>

        {tabs.length > 0 && (
          <div
            ref={headerRef}
            className="top-0 z-[3] w-full bg-[#F8F7F6] pt-[3rem] pb-[2rem] lg:sticky"
            style={{ top: offset }}>
            <div
              className="pointer-events-none absolute inset-x-0 bottom-full h-screen bg-[#F8F7F6]"
              aria-hidden
            />
            <motion.div
              {...fadeInUp(0.3)}
              viewport={{ once: true }}
              ref={tabsRowRef}
              role="tablist"
              className="flex gap-[.75rem] overflow-x-auto [scrollbar-width:none] lg:gap-[1.5rem] [&::-webkit-scrollbar]:hidden">
              {tabs.map((tab, index) => {
                const isActive = index === active;
                const accent = tab?.color || FALLBACK_ACCENT;

                return (
                  <button
                    key={index}
                    role="tab"
                    type="button"
                    aria-selected={isActive}
                    onClick={() => goToCard(index)}
                    style={{
                      backgroundColor: isActive ? accent : TAB_RESTING,
                    }}
                    className={cn(
                      "group relative flex h-[6.5rem] shrink-0 cursor-pointer items-center justify-center gap-[1rem] px-[3rem] py-[1.75rem] transition-colors duration-300 lg:flex-1 lg:shrink",
                      "[clip-path:polygon(0_0,100%_0,100%_calc(100%-1.75rem),calc(100%-1.75rem)_100%,0_100%)]",
                    )}>
                    {!isActive && (
                      <span
                        className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-30"
                        style={{ backgroundColor: accent }}
                        aria-hidden
                      />
                    )}

                    <span
                      className={cn(
                        "relative flex h-[3rem] w-[3rem] shrink-0 items-center justify-center rounded-full border font-geistmono text-[2rem] leading-none text-black/60 uppercase transition-colors duration-300",
                        isActive ? "border-black/[.11]" : "border-black/20",
                      )}>
                      {index + 1}
                    </span>
                    <span className="relative font-geistmono text-[2rem] leading-none text-black uppercase">
                      {tab?.label}
                    </span>
                  </button>
                );
              })}
            </motion.div>
          </div>
        )}

        {tabs.length > 0 && (
          <div
            ref={stackRef}
            className="relative z-[2] flex w-full snap-x snap-mandatory gap-[2rem] overflow-x-auto [scrollbar-width:none] lg:snap-none lg:flex-col lg:gap-[5rem] lg:overflow-visible [&::-webkit-scrollbar]:hidden">
            {tabs.map((tab, index) => (
              <div
                key={index}
                ref={(node) => {
                  cardRefs.current[index] = node;
                }}
                style={{ top: offset + headerHeight }}
                className="flex w-full shrink-0 snap-start flex-col bg-white lg:sticky">
                <div
                  className="h-[.25rem] w-full lg:h-[.5rem]"
                  style={{
                    backgroundColor: tab?.color || FALLBACK_ACCENT,
                  }}
                />
                <TabPanel tab={tab} />
              </div>
            ))}
          </div>
        )}

        {tabs.length > 1 && (
          <div className="relative z-[2] mt-[3rem] flex w-full items-center gap-[2rem] p-[1.5rem] lg:hidden">
            <div
              className="pointer-events-none absolute inset-0"
              aria-hidden>
              <div className="absolute inset-0 rounded-full bg-white" />
              <div className="absolute inset-0 rounded-full pb-[.25rem] pl-[.25rem]">
                <div className="h-full w-full rounded-full bg-[#D3D3D2]" />
              </div>
              <div className="absolute inset-0 rounded-full pt-px pr-px pb-[.375rem] pl-[.375rem]">
                <div className="h-full w-full rounded-full bg-white" />
              </div>
              <div className="absolute inset-0 rounded-full p-[.375rem]">
                <div className="h-full w-full rounded-full bg-[#F3F2F1]" />
              </div>
            </div>

            <button
              type="button"
              aria-label="Vorherige Karte"
              disabled={active === 0}
              onClick={() => goToCard(Math.max(active - 1, 0))}
              className={cn(
                "relative flex h-[7rem] w-[7rem] shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-300",
                active === 0 ? "bg-[#202223]/10 text-[#202223]" : "bg-[#202223] text-white",
              )}>
              <ChevronLeft className="h-[4rem] w-[4rem]" />
            </button>

            <div className="relative flex flex-1 items-center gap-[1rem]">
              {tabs.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "h-[.5rem] flex-1 bg-[#202223] transition-opacity duration-300",
                    index !== active && "opacity-20",
                  )}
                />
              ))}
            </div>

            <button
              type="button"
              aria-label="Nächste Karte"
              disabled={active === tabs.length - 1}
              onClick={() => goToCard(Math.min(active + 1, tabs.length - 1))}
              className={cn(
                "relative flex h-[7rem] w-[7rem] shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-300",
                active === tabs.length - 1 ? "bg-[#202223]/10 text-[#202223]" : "bg-[#202223] text-white",
              )}>
              <ChevronRight className="h-[4rem] w-[4rem]" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
